import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Award, ChevronRight, Calendar, ExternalLink, Search, Star, MessageSquare, Lock } from 'lucide-react';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import InstantBookingModal from '../../components/booking/InstantBookingModal';
import LawyerDetailModal from '../../components/lawyers/LawyerDetailModal';
import ReviewSubmissionModal from '../../components/reviews/ReviewSubmissionModal';
import ClientChatModal from '../../components/chat/ClientChatModal';
import SubscriptionModal from '../../components/subscription/SubscriptionModal';

export default function LawyersPage() {
  const { t, localized, currentLang } = useLanguage();
  const { isSubscribed } = useAuth();
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedLawyerForBooking, setSelectedLawyerForBooking] = useState(null);
  const [selectedLawyerForDetail, setSelectedLawyerForDetail] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedLawyerForReview, setSelectedLawyerForReview] = useState(null);
  const [selectedChatLawyer, setSelectedChatLawyer] = useState(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  useEffect(() => {
    api.get('/lawyers')
      .then((res) => {
        if (res?.success) {
          const sorted = [...(res.lawyers || [])]
            .filter((l) => Boolean(l.isActive) === true)
            .sort((a, b) =>
              (a.fullName || '').localeCompare(b.fullName || '', 'uz', { sensitivity: 'base' })
            );
          setLawyers(sorted);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleOpenDetail = (lawyer) => {
    setSelectedLawyerForDetail(lawyer);
  };

  const handleBookLawyer = (lawyer) => {
    setSelectedLawyerForBooking(lawyer);
    setBookingModalOpen(true);
  };

  return (
    <div className="bg-[#05080e] min-h-screen text-slate-200">
      {/* Header */}
      <section className="bg-[#070c16] text-white py-16 sm:py-24 border-b border-[#141d2d] text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
            {t('specialistsTag', 'ATTORNEYS & COUNSELORS AT LAW')}
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold text-slate-100">
            {t('sec_lawyers_title', 'Yetakchi Advokatlar Jamoasi')}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-4 font-light leading-relaxed">
            {t('sec_lawyers_desc', 'Oliy toifali litsenziyaga va boy amaliy tajribaga ega bo‘lgan yetakchi mutaxassislar.')}
          </p>

          {/* Search Bar */}
          <div className="mt-8 max-w-md mx-auto relative">
            <Search className="w-4 h-4 text-[#c59b27] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder={t('search_lawyers_placeholder', 'Advokat ismi, ixtisosligi yoki soha bo‘yicha izlash...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-9 py-2.5 bg-[#090e18] border border-[#1b2b46] rounded-xl text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#c59b27] shadow-lg transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 sm:py-24 bg-[#05080e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20 text-slate-400">{t('searching', 'Yuklanmoqda...')}</div>
          ) : lawyers.filter((l) => {
              if (!searchQuery.trim()) return true;
              const q = searchQuery.toLowerCase();
              return (
                l.fullName?.toLowerCase().includes(q) ||
                l.specialization?.toLowerCase().includes(q) ||
                l.titleUz?.toLowerCase().includes(q) ||
                l.titleRu?.toLowerCase().includes(q) ||
                l.titleEn?.toLowerCase().includes(q) ||
                l.bioUz?.toLowerCase().includes(q)
              );
            }).length === 0 ? (
            <div className="text-center py-20 text-slate-400">Qidiruv bo‘yicha advokat topilmadi</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {lawyers.filter((l) => {
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase();
                return (
                  l.fullName?.toLowerCase().includes(q) ||
                  l.specialization?.toLowerCase().includes(q) ||
                  l.titleUz?.toLowerCase().includes(q) ||
                  l.titleRu?.toLowerCase().includes(q) ||
                  l.titleEn?.toLowerCase().includes(q) ||
                  l.bioUz?.toLowerCase().includes(q)
                );
              }).map((lawyer) => (
                <div
                  key={lawyer.id}
                  className="bg-[#090e18] rounded-xl border border-[#1b2b46] overflow-hidden shadow-2xl hover:border-[#c59b27]/60 hover:-translate-y-1.5 transition-all duration-300 group flex flex-col justify-between"
                >
                  <div 
                    onClick={() => handleOpenDetail(lawyer)} 
                    className="cursor-pointer"
                  >
                    <div className="relative h-80 overflow-hidden bg-[#0c1424]">
                      <img
                        src={lawyer.photoUrl || "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=600"}
                        alt={lawyer.fullName}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#090e18] via-transparent to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4">
                        <span className="text-[10px] uppercase tracking-wider text-[#c59b27] font-semibold block">
                          {lawyer.experienceYears} {t('lawyer_experience_suffix', 'yillik tajriba')}
                        </span>
                        <h3 className="font-serif text-lg font-bold text-white truncate">
                          {lawyer.fullName}
                        </h3>
                      </div>
                    </div>

                    <div className="p-5">
                      <p className="text-xs text-[#c59b27] font-medium mb-3">
                        {lawyer.position || localized(lawyer, 'position') || 'Advokat'}
                      </p>
                      <p className="text-slate-400 text-xs line-clamp-3 leading-relaxed font-light">
                        {localized(lawyer, 'bio') || lawyer.bioUz}
                      </p>
                    </div>
                  </div>

                  <div className="p-5 pt-0">
                    <div className="pt-4 border-t border-[#141d2d] flex items-center justify-between text-xs gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenDetail(lawyer)}
                        className="font-semibold text-slate-300 hover:text-[#c59b27] inline-flex items-center transition-colors cursor-pointer"
                      >
                        {t('lawyer_detail_btn', 'Batafsil →')}
                      </button>

                      {/* Lawyer Rating Star Button (Professional Sharp 5-Pointed Star SVG) */}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedLawyerForReview(lawyer);
                          setReviewModalOpen(true);
                        }}
                        className="w-6 h-6 rounded-full border border-[#c59b27]/80 hover:border-[#d4af37] bg-gradient-to-b from-[#0e1728] to-[#070c16] hover:from-[#14223a] hover:to-[#0a1220] flex items-center justify-center shadow-sm shadow-black/50 transition-all cursor-pointer group active:scale-95"
                        title={`${lawyer.fullName} faoliyatiga baho berish`}
                        aria-label={`${lawyer.fullName} faoliyatiga baho berish`}
                      >
                        <svg
                          className="w-3.5 h-3.5 group-hover:scale-110 transition-transform drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <defs>
                            <linearGradient id={`starGrad-lawyers-${lawyer.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#fef08a" />
                              <stop offset="50%" stopColor="#eab308" />
                              <stop offset="100%" stopColor="#a16207" />
                            </linearGradient>
                          </defs>
                          <polygon
                            points="12,1.75 15.05,8.25 22.25,9.15 16.95,14.1 18.35,21.25 12,17.75 5.65,21.25 7.05,14.1 1.75,9.15 8.95,8.25"
                            fill={`url(#starGrad-lawyers-${lawyer.id})`}
                            stroke="#b48316"
                            strokeWidth="0.75"
                            strokeLinejoin="miter"
                          />
                        </svg>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedChatLawyer(lawyer);
                          setChatModalOpen(true);
                        }}
                        className="text-[11px] font-semibold text-[#c59b27] hover:text-amber-300 flex items-center gap-1 cursor-pointer transition-colors"
                        title={`${lawyer.fullName} bilan shaxsiy chat`}
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span>{currentLang === 'ru' ? 'Чат' : 'Chat'}</span>
                        {!isSubscribed && <Lock className="w-2.5 h-2.5 text-amber-400" />}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleBookLawyer(lawyer)}
                        className="px-3 py-1.5 rounded bg-[#b38038] hover:bg-[#c59b27] text-white text-[11px] font-bold uppercase tracking-wider transition-colors cursor-pointer"
                      >
                        {t('bookConsultation', 'Qabulga yozilish')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Comprehensive Lawyer Dossier Modal */}
      <LawyerDetailModal
        isOpen={!!selectedLawyerForDetail}
        onClose={() => setSelectedLawyerForDetail(null)}
        lawyer={selectedLawyerForDetail}
        onBookAppointment={(l) => {
          setSelectedLawyerForDetail(null);
          handleBookLawyer(l);
        }}
        onChatLawyer={(l) => {
          setSelectedLawyerForDetail(null);
          setSelectedChatLawyer(l);
          setChatModalOpen(true);
        }}
        onOpenSubscription={() => {
          setSelectedLawyerForDetail(null);
          setSubscriptionModalOpen(true);
        }}
      />

      {/* Instant Booking Modal */}
      <InstantBookingModal 
        isOpen={bookingModalOpen} 
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedLawyerForBooking(null);
        }} 
        initialLawyerId={selectedLawyerForBooking?.id}
      />

      {/* Lawyer Rating & Review Modal ("i" button) */}
      <ReviewSubmissionModal
        isOpen={reviewModalOpen}
        onClose={() => {
          setReviewModalOpen(false);
          setSelectedLawyerForReview(null);
        }}
        lawyers={lawyers}
        initialLawyerId={selectedLawyerForReview?.id}
      />

      {/* Client 1-on-1 Chat Modal */}
      <ClientChatModal
        isOpen={chatModalOpen}
        onClose={() => {
          setChatModalOpen(false);
          setSelectedChatLawyer(null);
        }}
        initialLawyer={selectedChatLawyer}
        onOpenSubscription={() => setSubscriptionModalOpen(true)}
      />

      {/* Subscription & Payment Modal */}
      <SubscriptionModal
        isOpen={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        initialType="CLIENT"
        onSuccess={() => {
          if (selectedChatLawyer) {
            setChatModalOpen(true);
          }
        }}
      />
    </div>
  );
}
